import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { RequireVidimost } from './RequireVidimost';
import { getTelegramInitData, initTelegramWebApp, isTwaMode, webAppAuth } from './webappAuth';

interface Props {
  children: ReactNode;
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: '50vh',
        display: 'grid',
        placeItems: 'center',
        color: '#683DFF',
        textAlign: 'center',
        padding: '1rem',
      }}
    >
      {children}
    </div>
  );
}

export function RequireAuth({ children }: Props) {
  const { isAuthenticated, isLoading, refetch } = useAuth();
  const location = useLocation();
  const twa = isTwaMode(location.search);
  const initData = useMemo(() => (twa ? getTelegramInitData() : null), [twa]);
  const [twaFailed, setTwaFailed] = useState(false);
  const triedRef = useRef(false);

  // Telegram Web App: вместо редиректа на пароль-логин — вход по initData.
  useEffect(() => {
    if (isLoading || isAuthenticated || !twa || !initData || triedRef.current) {
      return;
    }
    triedRef.current = true;
    initTelegramWebApp();
    webAppAuth(initData)
      .then(() => refetch())
      .catch(() => setTwaFailed(true));
  }, [isLoading, isAuthenticated, twa, initData, refetch]);

  if (isLoading) {
    return <Centered>Загрузка…</Centered>;
  }

  if (!isAuthenticated) {
    // В Telegram Web App с валидным initData — показываем вход, не уводим на /login.
    if (twa && initData && !twaFailed) {
      return <Centered>Открываю стол…</Centered>;
    }
    if (twa && twaFailed) {
      return (
        <Centered>
          Не удалось открыть стол: этот Telegram не привязан к аккаунту или подпись
          устарела. Обратитесь к владельцу.
        </Centered>
      );
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // ⛔ `prv3`: слой видимости проверяется ОДНИМ местом на всё дерево маршрутов —
  // внутри входа, сразу после того как известно, кто перед нами.
  return <RequireVidimost>{children}</RequireVidimost>;
}
